import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
    MapPin,
    Clock,
    Instagram,
    ShoppingBag,
    Send,
    Loader2,
    CheckCircle2,
    MessageCircle,
} from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { SHOP } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function isOpenNow() {
    const hour = new Date().getHours();
    return hour >= 11 && hour < 21;
}

function InfoCard({ icon: Icon, title, children, testId }) {
    return (
        <div
            data-testid={testId}
            className="rounded-3xl border border-ink/5 bg-white p-6 shadow-soft"
        >
            <div className="flex items-center gap-3.5">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-soft text-accent">
                    <Icon className="h-4.5 w-4.5 h-5 w-5" />
                </span>
                <h3 className="font-display text-lg font-bold tracking-tight text-ink">
                    {title}
                </h3>
            </div>
            <div className="mt-4 text-sm leading-relaxed text-ink-muted">
                {children}
            </div>
        </div>
    );
}

export default function Contact() {
    const [form, setForm] = useState({ name: "", contact: "", message: "" });
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [waLinkUrl, setWaLinkUrl] = useState("");
    const open = isOpenNow();

    const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const waLink = (f) =>
        `https://wa.me/6281929550751?text=${encodeURIComponent(
            `Halo Yuhuku Kopi! Saya ${f.name}.\nPesan: ${f.message}\nKontak saya: ${f.contact}`
        )}`;

    const onSubmit = async (e) => {
        e.preventDefault();
        if (sending) return;
        setSending(true);
        try {
            await axios.post(`${API}/messages`, form);
            setSent(true);
            setWaLinkUrl(waLink(form));
            window.open(waLink(form), "_blank", "noopener");
            setForm({ name: "", contact: "", message: "" });
            toast.success("Pesan terkirim! Lanjutkan ke WhatsApp pemilik cafe.");
        } catch {
            toast.error("Gagal mengirim pesan. Coba lagi ya.");
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="pt-24 lg:pt-32" data-testid="contact-page">
            <header className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <Reveal>
                    <span className="eyebrow">Ada acara? Mampir!</span>
                </Reveal>
                <Reveal delay={0.08}>
                    <h1 className="mt-4 font-display text-5xl font-extrabold tracking-tighter text-ink sm:text-6xl lg:text-7xl">
                        Say <span className="text-accent">Hello.</span>
                    </h1>
                </Reveal>
                <Reveal delay={0.16}>
                    <p className="mt-5 max-w-xl leading-relaxed text-ink-muted">
                        Kirim pesan, DM Instagram, atau langsung mampir ke
                        cafe. Kami balas secepat kopinya diseduh.
                    </p>
                </Reveal>
            </header>

            <div className="mx-auto grid max-w-7xl gap-8 px-4 py-14 sm:px-6 lg:grid-cols-12 lg:px-8 lg:py-20">
                <div className="lg:col-span-7">
                    <Reveal>
                        <div className="rounded-3xl border border-ink/5 bg-white p-6 shadow-soft sm:p-8">
                            <h2 className="font-display text-2xl font-extrabold tracking-tight text-ink">
                                Kirim Pesan
                            </h2>
                            <p className="mt-1.5 text-sm text-ink-muted">
                                Buat pesanan besar, acara, atau sekadar sapaan.
                            </p>
                            <form
                                onSubmit={onSubmit}
                                data-testid="contact-form"
                                className="mt-7"
                            >
                                <label className="block">
                                    <span className="mb-2 block text-sm font-semibold text-ink">
                                        Nama
                                    </span>
                                    <input
                                        data-testid="contact-input-name"
                                        name="name"
                                        value={form.name}
                                        onChange={onChange}
                                        required
                                        maxLength={100}
                                        placeholder="Namamu"
                                        className="input-clean"
                                    />
                                </label>
                                <label className="mt-5 block">
                                    <span className="mb-2 block text-sm font-semibold text-ink">
                                        Email / No. WhatsApp
                                    </span>
                                    <input
                                        data-testid="contact-input-contact"
                                        name="contact"
                                        value={form.contact}
                                        onChange={onChange}
                                        required
                                        maxLength={100}
                                        placeholder="08xx... atau email@kamu.com"
                                        className="input-clean"
                                    />
                                </label>
                                <label className="mt-5 block">
                                    <span className="mb-2 block text-sm font-semibold text-ink">
                                        Pesan
                                    </span>
                                    <textarea
                                        data-testid="contact-input-message"
                                        name="message"
                                        value={form.message}
                                        onChange={onChange}
                                        required
                                        maxLength={1000}
                                        rows={5}
                                        placeholder="Mau pesan apa hari ini?"
                                        className="input-clean resize-none"
                                    />
                                </label>
                                <motion.button
                                    whileTap={{ scale: 0.98 }}
                                    type="submit"
                                    disabled={sending}
                                    data-testid="contact-form-submit"
                                    className="btn-primary mt-7 w-full"
                                >
                                    {sending ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <Send className="h-4 w-4" />
                                    )}
                                    {sending ? "Mengirim..." : "Kirim Pesan"}
                                </motion.button>
                                {sent && (
                                    <div
                                        data-testid="contact-form-success"
                                        className="mt-5 rounded-2xl border border-matcha/30 bg-matcha/10 p-4"
                                    >
                                        <p className="flex items-center gap-2 text-sm font-semibold text-ink">
                                            <CheckCircle2 className="h-4 w-4 text-matcha" />
                                            Pesanmu sudah masuk. Terima kasih!
                                        </p>
                                        <a
                                            data-testid="contact-wa-button"
                                            href={waLinkUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:brightness-95"
                                        >
                                            <MessageCircle className="h-4 w-4" />
                                            Lanjut ke WhatsApp Pemilik
                                        </a>
                                        <p className="mt-2 text-center text-xs text-ink-muted">
                                            Halaman WhatsApp terbuka otomatis —
                                            kalau tidak, tekan tombol di atas.
                                        </p>
                                    </div>
                                )}
                            </form>
                        </div>
                    </Reveal>
                </div>

                <div className="flex flex-col gap-5 lg:col-span-5">
                    <Reveal delay={0.08}>
                        <InfoCard icon={MapPin} title="Alamat" testId="contact-ticket-address">
                            <p className="font-medium text-ink">{SHOP.address}</p>
                            <a
                                href={SHOP.mapsUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="contact-link-maps"
                                className="mt-2 inline-flex font-semibold text-accent hover:underline"
                            >
                                Lihat di Google Maps
                            </a>
                            <div className="mt-4 h-52 w-full overflow-hidden rounded-2xl border border-ink/5">
                                <iframe
                                    title="Peta lokasi Yuhuku Kopi"
                                    src={SHOP.mapsEmbed}
                                    loading="lazy"
                                    className="h-full w-full"
                                    data-testid="contact-map-iframe"
                                />
                            </div>
                        </InfoCard>
                    </Reveal>

                    <Reveal delay={0.14}>
                        <InfoCard icon={Clock} title="Jam Buka" testId="contact-ticket-hours">
                            <p className="flex items-center gap-2">
                                <span
                                    data-testid="contact-open-status"
                                    className={`inline-block h-2.5 w-2.5 rounded-full ${
                                        open ? "bg-matcha" : "bg-accent"
                                    }`}
                                />
                                {open
                                    ? "Sedang buka — mampir sekarang!"
                                    : "Sedang tutup — buka jam 11.00"}
                            </p>
                            <p className="mt-2 font-display text-xl font-extrabold text-ink">
                                Setiap hari, 11.00–21.00
                            </p>
                        </InfoCard>
                    </Reveal>

                    <Reveal delay={0.2}>
                        <InfoCard icon={Instagram} title="Sosial & Pesan" testId="contact-ticket-social">
                            <div className="flex flex-col gap-3">
                                <a
                                    href={SHOP.whatsapp}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="contact-link-whatsapp"
                                    className="flex items-center gap-2.5 font-semibold text-ink transition-colors hover:text-accent"
                                >
                                    <MessageCircle className="h-4 w-4 text-accent" />
                                    WhatsApp Pemilik ({SHOP.whatsappDisplay})
                                </a>
                                <a
                                    href={SHOP.instagram}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="contact-link-instagram"
                                    className="flex items-center gap-2.5 font-semibold text-ink transition-colors hover:text-accent"
                                >
                                    <Instagram className="h-4 w-4 text-accent" />
                                    {SHOP.instagramHandle}
                                </a>
                                <a
                                    href={SHOP.gofood}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="contact-link-gofood"
                                    className="flex items-center gap-2.5 font-semibold text-ink transition-colors hover:text-accent"
                                >
                                    <ShoppingBag className="h-4 w-4 text-accent" />
                                    Pesan di GoFood
                                </a>
                            </div>
                        </InfoCard>
                    </Reveal>
                </div>
            </div>
        </div>
    );
}